import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, IconButton, SxProps, TextField, Theme } from '@mui/material';
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import { useSnackbar } from 'notistack';

import { useLanguage } from '../../hooks';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { AlertTriangleOutline } from '../../icons';
import { useAppSelector } from '../../store/hooks';
import { datascopeTracker } from '../../utils/eventTracker';
import { PhotoQuestionIcon, TextQuestionIcon } from '../icons';
import { Checkbox } from '../uiComponents/ComponentLibrary/Checkbox';
import { InfoAlert } from '../uiComponents/infoAlert';
import { barCodeTypes } from './barCodeTypes';
import { LimitSubformsOption } from './limitSubformsOption';
import NumberPercentSetting from './numberPercentSetting';
import { questionTypesList } from './questionTypes';
import { SkipLogicComponent } from './skipLogicComponent';
import TextValidationSetting from './textValidationSetting';

const customStyles: Record<string, SxProps<Theme>> = {
  mainContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  setting: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '22px',
    '&:last-child': {
      marginRight: '0px',
    },
  },
  label: {
    fontSize: '14px',
    lineHeight: '22px',
  },
  expandable: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    paddingLeft: '0.25em',

    '&:hover': {
      backgroundColor: 'rgba(32,33,36,0.039)',
      borderRadius: '3px',
    },
  },
  closeIcon: {
    transition: 'transform .1s ease-in-out',
    transform: 'rotate(180deg)',
    MsTransform: 'rotate(180deg)',
    WebkitTransform: 'rotate(180deg)',
  },
  openIcon: {
    transition: 'transform .1s ease-out',
  },
  popover: {
    '& .MuiPopover-paper': {
      borderRadius: '8px',
    },
  },
  toggletip: {
    backgroundColor: '#FFFFFF',
    color: '#3B3C40',
    maxWidth: '240px',
    maxHeight: '400px',
    fontSize: '14px',
    overflowY: 'auto',
    lineHeight: '22px',
    boxshadow: '2px 4px 16px rgba(15, 59, 96, 0.18)',
    borderRadius: '8px',
    padding: '12px',
  },
  toggletipInfo: {
    fontSize: '14px',
    marginBottom: '16px',
    cursor: 'auto',
  },
  toggletipLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '14px',
      display: '-webkit-box',
      lineHeight: '22px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      WebkitLineClamp: '2px',
      WebkitBoxOrient: 'vertical',
      wordWrap: 'break-word',
      lineClamp: '2px',
    },
  },
  commentOptionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  commentAlternatives: {
    flexShrink: 0,
  },
  commentAlternativeBtn: {
    fontSize: 18,
  },
  commentTextBtn: {
    fill: '#B8BABF',
  },
  commentImageBtn: {
    stroke: '#B8BABF',
  },
  commentTextRequired: {
    fill: '#3B3C40',
  },
  mobileOnlyAlert: {
    margin: '24px 0px',
  },
  textFieldMargin: {
    marginRight: '8px',
  },
  textFieldInputPadding: {
    padding: '4px 8px',
  },
};

const QuestionSetting = memo(function QuestionSetting(props: {
  question: any;
  onRequiredChange: any;
  onTextValidationChange: (
    questionId: string,
    validation: 'email' | 'rut-chile' | 'container' | null,
  ) => void;
  onNumberPercentageChange: (
    questionId: string,
    show: 'percent' | 'sampling' | null,
    tableId?: string,
  ) => void;
  onBarcodeTypeChange: any;
  onDisableGalleryChange: any;
  onMultiPhotoChange: any;
  onSkipLogicChange: any;
  onSkipSettingChange: any;
  logicQuestions: any;
  paying: boolean;
  numberMetadataSetting: any;
  onRelatedListChange: (id: string, action: string, value: string) => void;
  onOptionsWithCommentsChange: (id: string) => void;
  onObligatoryOptionCommentChange: (
    id: string,
    index: number,
    reqText: boolean,
    reqImage: boolean,
    reqFinding: boolean,
  ) => void;
  onSubformsLimitChange: (id: string, value: string, type: string) => void;
  onChecklistSettingsChange: (
    questionId: string,
    disableGallery?: boolean,
    maxImages?: number,
    tableId?: string,
  ) => void;
  onMeterChange: (questionId: string, value: string, tableId?: string) => void;
  onDateValidationCheckboxChange: (id: string) => void;
}) {
  const {
    question,
    onRequiredChange,
    onTextValidationChange,
    onNumberPercentageChange,
    onBarcodeTypeChange,
    onDisableGalleryChange,
    onMultiPhotoChange,
    onSkipLogicChange,
    onSkipSettingChange,
    logicQuestions,
    paying,
    numberMetadataSetting,
    onRelatedListChange,
    onOptionsWithCommentsChange,
    onObligatoryOptionCommentChange,
    onSubformsLimitChange,
    onChecklistSettingsChange,
    onMeterChange,
    onDateValidationCheckboxChange,
  } = props;
  const [openedAlert, setOpenedAlert] = useState(false);
  const [checklistMaxImages, setChecklistMaxImages] = useState(question?.maxImages ?? 1);
  const [searchParams] = useSearchParams();

  const lang = useLanguage();
  const { formId } = useParams<{ formId: string }>();

  const { checkFlag, getPremiumFlag } = useFeatureFlags();
  const multiPhoto = checkFlag('MultiPhoto');
  const fileQuestionRequired = checkFlag('FileQuestionRequired');
  const findingsActivated = !!getPremiumFlag('Findings');
  const findingsCommentRequiredFlag = checkFlag('FindingsCommentRequired');
  const dateQuestionValidationFlag = checkFlag('DateQuestionValidation');

  const [toggletipAnchor, setToggletipAnchor] = useState<HTMLDivElement | null>(null);

  const { globalObjectsTotals } = useAppSelector((state) => state.listObjects);
  const { total: totalLocationItems } = useAppSelector((state) => state.locations);
  const { rightColumn } = useAppSelector((state) => state.listManager);
  const { enqueueSnackbar } = useSnackbar();

  const totalOptionItems = useMemo(
    () => globalObjectsTotals[question?.metadata],
    [globalObjectsTotals, question?.metadata],
  );
  const safeToReqImage = useMemo(() => {
    // We need to check the items limit is in total below the max of 500 images
    // Coordinated with Mobile Team
    const noMetadataSelected = !question?.metadata;
    const locationsListSelected = question?.metadata === 'locations';
    const metadataListSelected = question?.metadata && question?.metadata !== 'locations';
    const safeToReqImage =
      noMetadataSelected ||
      (locationsListSelected && (question?.maxImages ?? 10) * totalLocationItems < 500) ||
      (metadataListSelected && (question?.maxImages ?? 10) * totalOptionItems < 500);
    return safeToReqImage;
  }, [totalLocationItems, totalOptionItems, question?.metadata, question?.maxImages]);

  useEffect(() => {
    if (!question?.options) return;
    if (!question?.metadata) return;
    if (globalObjectsTotals[question.metadata] * (question?.maxImages ?? 1) > 500) {
      let atLeastOneImageIsMandatory = false;
      // We remove the mandatory image comments for all the options of the question:
      question?.options?.map((option: any, index: number) => {
        // Check if option is an object and has the req_text property
        const isObjectAndNotString =
          typeof option === 'object' && option !== null && !(option instanceof String);
        const reqText = isObjectAndNotString && 'req_text' in option ? option.req_text : false;
        const reqImage = isObjectAndNotString && 'req_image' in option ? option.req_image : false;
        const reqFinding =
          isObjectAndNotString && 'req_finding' in option ? option.req_finding : false;
        atLeastOneImageIsMandatory = atLeastOneImageIsMandatory || reqImage;

        onObligatoryOptionCommentChange(
          question.id,
          index,
          reqText, // This one stays the same
          false, //default to false
          reqFinding,
        );
      });
      if (atLeastOneImageIsMandatory) {
        enqueueSnackbar(lang.form_builder.imageDisabledByLimit, {
          variant: 'warning',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
        });
      }
    }
  }, [globalObjectsTotals, question?.metadata, question?.maxImages]);

  useEffect(() => {
    if (!question?.options) return;
    if (!question?.metadata || question?.metadata !== 'locations') return;
    // For when the locations list is chosen:
    if (totalLocationItems * (question?.maxImages ?? 10) > 500) {
      question.options.map((option: any, index: number) => {
        // Check if option is an object and has the req_text property
        const isObjectAndNotString =
          typeof option === 'object' && option !== null && !(option instanceof String);
        const reqText = isObjectAndNotString && 'req_text' in option ? option.req_text : false;
        const reqFinding =
          isObjectAndNotString && 'req_finding' in option ? option.req_finding : false;
        onObligatoryOptionCommentChange(question.id, index, reqText, false, reqFinding);
      });
    }
  }, [totalLocationItems, question?.metadata, question?.maxImages]);

  const handleOpenConfigToggletip = (
    event: React.MouseEvent<HTMLDivElement>,
    questionType: string,
    questionId: string,
  ) => {
    if (questionType === 'select_option_metadata') {
      onOptionsWithCommentsChange(questionId);
    }
    setToggletipAnchor(event.currentTarget);
  };

  const handleObligatoryCommentCheckbox = useCallback(
    (index: number, option: any) => {
      onObligatoryOptionCommentChange(
        question.id,
        index,
        !(option.req_text || option.req_image || option.req_finding),
        safeToReqImage ? !(option.req_text || option.req_image || option.req_finding) : false,
        !(option.req_text || option.req_image || option.req_finding) &&
          findingsActivated &&
          findingsCommentRequiredFlag,
      );
    },
    [safeToReqImage, findingsActivated, findingsCommentRequiredFlag],
  );

  const handleOnChecklistSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMaxImages =
      parseInt(event.target.value) > 10
        ? '10'
        : parseInt(event.target.value) < 1
        ? '1'
        : event.target.value === ''
        ? '1'
        : event.target.value;
    onChecklistSettingsChange(question.id, question.disableGallery, parseInt(newMaxImages) ?? 1);
    setChecklistMaxImages(newMaxImages);
  };

  const handleCloseConfigToggletip = () => {
    setToggletipAnchor(null);
  };

  const handleMeterCheckboxChange = () => {
    datascopeTracker(
      'update_meter',
      {
        form_id: formId,
        list_id: question.metadata,
        update_meter: !question.meterId,
      },
      'AMP',
    );
    onMeterChange(question.id, '-1');
  };

  const openToggleTip = Boolean(toggletipAnchor);
  const toggleTipId = openToggleTip ? 'obligatory-comments' : undefined;

  return (
    <CardContent sx={{ padding: '4px 16px 4px' }}>
      <Box sx={customStyles.mainContainer}>
        {[
          'text',
          'number',
          'date',
          'signature',
          'barcode',
          'photo',
          'multi_photo',
          'multi_option',
          'select_metadata',
          'select_number_metadata',
          'select_text_metadata',
          'select_option_metadata',
          'select_option_metadata_comments',
          'select_activity_period_time',
          ...(fileQuestionRequired ? ['attachment'] : []),
        ].includes(question.type) && (
          <Box sx={customStyles.setting}>
            <Checkbox
              disableTouchRipple
              size={'small'}
              color={'default'}
              checked={question.req === '1'}
              onChange={() => onRequiredChange(question.id)}
            />
            <Box sx={customStyles.label}>{lang.form_builder.required}</Box>
          </Box>
        )}
        {question.type === 'barcode' && (
          <Box sx={{ margin: '0 15px 0' }}>
            <Select
              disableUnderline
              value={question.barcodeType ? question.barcodeType : 'N/A'}
              onChange={(e: any) => onBarcodeTypeChange(question.id, e.target.value)}
              renderValue={(value) => {
                if (value === 'N/A')
                  return (
                    <Tooltip title={lang.form_builder.codeType}>
                      <Box sx={{ fontSize: 12, paddingTop: 5 }}>N/A</Box>
                    </Tooltip>
                  );
                return (
                  <Tooltip title={lang.form_builder.codeType}>
                    <Box sx={{ fontSize: 12, paddingTop: 5 }}>
                      {barCodeTypes.find((type) => type.value === value).tag}
                    </Box>
                  </Tooltip>
                );
              }}
            >
              {barCodeTypes.map((type) => (
                <MenuItem
                  sx={{ height: 18 }}
                  value={type.value}
                >
                  <Box sx={{ fontSize: 12, paddingTop: 5 }}>{type.tag}</Box>
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
        {(question.type === 'photo' || question.type === 'multi_photo') && (
          <>
            <Box sx={customStyles.setting}>
              <Checkbox
                disableTouchRipple
                size={'small'}
                color={'default'}
                checked={question.disableGallery === '1'}
                onChange={() => onDisableGalleryChange(question.id)}
              />
              <Box sx={customStyles.label}>{lang.form_builder.disableGallery}</Box>
            </Box>
            {multiPhoto && (
              <Box sx={customStyles.setting}>
                <Checkbox
                  disableTouchRipple
                  size={'small'}
                  color={'default'}
                  checked={question.type === 'multi_photo'}
                  onChange={() => onMultiPhotoChange(question.id)}
                />
                <Box sx={customStyles.label}>{lang.form_builder.multi_photo}</Box>
              </Box>
            )}
          </>
        )}
        {question.type === 'text' && (
          <TextValidationSetting
            validateMail={question.mail}
            validateRutChile={question.rutValidation}
            validateContainer={question.containerValidation}
            onValidationChange={(validation) => onTextValidationChange(question.id, validation)}
          />
        )}
        {searchParams.get('number_count_config') === 'true' &&
          question.type === 'select_number_metadata' && (
            <Box sx={customStyles.setting}>
              <Checkbox
                disableTouchRipple
                size={'small'}
                color={'default'}
                checked={question.asCount === '1'}
                onChange={() => numberMetadataSetting(question.id, 'asCount')}
              />
              <Box sx={customStyles.label}>{lang.form_builder.asCount}</Box>
            </Box>
          )}
        {question.type === 'select_number_metadata' && (
          <>
            <Box sx={customStyles.setting}>
              <Checkbox
                disableTouchRipple
                size={'small'}
                color={'default'}
                checked={question.showPrice === '1'}
                onChange={() => numberMetadataSetting(question.id, 'showPrice')}
              />
              <Box sx={customStyles.label}>{lang.form_builder.showPrice}</Box>
            </Box>
            <Box sx={customStyles.setting}>
              <Checkbox
                disableTouchRipple
                size={'small'}
                color={'default'}
                checked={question.showAvg === '1'}
                onChange={() => numberMetadataSetting(question.id, 'showAvg')}
              />
              <Box sx={customStyles.label}>{lang.form_builder.showAvg}</Box>
            </Box>
            <Box sx={customStyles.setting}>
              <Checkbox
                disableTouchRipple
                size={'small'}
                color={'default'}
                checked={question.showFactor === '1'}
                onChange={() => numberMetadataSetting(question.id, 'showFactor')}
              />
              <Box sx={customStyles.label}>{lang.form_builder.showFactor}</Box>
            </Box>
            {checkFlag('ShowSamplingPercent') ? (
              <NumberPercentSetting
                showPercent={question.showPercent === '1'}
                showSampling={question.showSampling === '1'}
                onShowChange={(show) => onNumberPercentageChange(question.id, show)}
              />
            ) : (
              <Box sx={customStyles.setting}>
                <Checkbox
                  disableTouchRipple
                  size={'small'}
                  color={'default'}
                  checked={question.showPercent === '1'}
                  onChange={() => numberMetadataSetting(question.id, 'showPercent')}
                />
                <Box sx={customStyles.label}>{lang.form_builder.showPercent}</Box>
              </Box>
            )}
          </>
        )}

        {questionTypesList.includes(question.type) && (
          <Box sx={customStyles.setting}>
            <Checkbox
              disableTouchRipple
              size={'small'}
              color={'default'}
              checked={question.skipLogic === '1'}
              onChange={() => {
                if (paying) onSkipLogicChange(question.id);
                else setOpenedAlert(true);
              }}
            />
            <Box sx={customStyles.label}>{lang.form_builder.skipQuestions}</Box>
            <Dialog
              open={openedAlert}
              maxWidth={'xs'}
              onClose={() => setOpenedAlert(false)}
            >
              <DialogContent>
                <DialogContentText>
                  <Box sx={{ textAlign: 'center', fontSize: 14 }}>
                    {lang.form_builder.payingAlert}
                  </Box>
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'center' }}>
                <Button
                  onClick={() => setOpenedAlert(false)}
                  color={'primary'}
                  size={'small'}
                  sx={{ textTransform: 'capitalize' }}
                  variant={'outlined'}
                >
                  {lang.form_builder.ok}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
        {question.type === 'date' && dateQuestionValidationFlag && (
          <Box sx={customStyles.setting}>
            <Checkbox
              disableTouchRipple
              size={'small'}
              color={'default'}
              checked={question.dateValidationType !== undefined}
              onChange={() => onDateValidationCheckboxChange(question.id)}
            />
            <Box sx={customStyles.label}>{lang.form_builder.dateValidation.addValidation}</Box>
          </Box>
        )}
        {['multi_option', 'select_metadata'].includes(question.type) && (
          <Box sx={customStyles.setting}>
            <Checkbox
              disableTouchRipple
              size={'small'}
              color={'default'}
              checked={question.related_list === '1'}
              onChange={() => onRelatedListChange(question.id, 'related:list', undefined)}
            />
            <Box sx={customStyles.label}>{lang.form_builder.relatedList}</Box>
          </Box>
        )}
        {checkFlag('Assets') && question.type === 'select_metadata' && rightColumn === 'ASSETS' && (
          <Box sx={customStyles.setting}>
            <Checkbox
              disableTouchRipple
              size={'small'}
              color={'default'}
              checked={!!question.meterId}
              onChange={handleMeterCheckboxChange}
            />
            <Box sx={customStyles.label}>{lang.form_builder.updateMeter}</Box>
          </Box>
        )}
        {question.type === 'table' && (
          <LimitSubformsOption
            paying={paying}
            styles={customStyles}
            questionId={question.id}
            max_subforms={question.max_subforms}
            min_subforms={question.min_subforms}
            onSubformsLimitChange={onSubformsLimitChange}
          />
        )}
        {['select_option_metadata', 'select_option_metadata_comments'].includes(question.type) && (
          <Box sx={customStyles.setting}>
            <Checkbox
              disableTouchRipple
              size={'small'}
              color={'default'}
              checked={question.type === 'select_option_metadata_comments'}
              onChange={() => onOptionsWithCommentsChange(question.id)}
            />
            <Box
              sx={customStyles.expandable}
              onClick={(e) => handleOpenConfigToggletip(e, question.type, question.id)}
            >
              <Box sx={customStyles.label}>{lang.form_builder.includeComments}</Box>
              <ExpandMoreIcon sx={openToggleTip ? customStyles.closeIcon : customStyles.openIcon} />
            </Box>
            <Popover
              id={toggleTipId}
              open={openToggleTip}
              anchorEl={toggletipAnchor}
              onClose={handleCloseConfigToggletip}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              sx={customStyles.popover}
            >
              <Box sx={customStyles.toggletip}>
                <FormControl component='fieldset'>
                  <Box sx={customStyles.toggletipInfo}>{lang.form_builder.commentsFormLabel}</Box>
                  <FormGroup>
                    {question.options.map((option: any, index: number) => {
                      return (
                        <Box
                          key={`optionReq-${index}`}
                          sx={customStyles.commentOptionRow}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                disableTouchRipple
                                size={'small'}
                                color={'default'}
                                checked={option.req_text || option.req_image || option.req_finding}
                                key={`optionReq-${index}`}
                                onChange={() => handleObligatoryCommentCheckbox(index, option)}
                                name={option.option}
                              />
                            }
                            label={option.option}
                            sx={customStyles.toggletipLabel}
                          />
                          <Box sx={customStyles.commentAlternatives}>
                            <Tooltip title={lang.form_builder.textCommentBtnTooltip}>
                              <IconButton
                                size='small'
                                onClick={() =>
                                  onObligatoryOptionCommentChange(
                                    question.id,
                                    index,
                                    !option.req_text,
                                    option.req_image,
                                    option.req_finding,
                                  )
                                }
                              >
                                <TextQuestionIcon
                                  sx={
                                    {
                                      ...customStyles.commentAlternativeBtn,
                                      ...customStyles.commentTextBtn,
                                      ...(option.req_text && customStyles.commentTextRequired),
                                    } as SxProps<Theme>
                                  }
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                safeToReqImage
                                  ? lang.form_builder.imageCommentBtnTooltip
                                  : lang.form_builder.imageCommentBtnTooltipDisabled
                              }
                            >
                              <span>
                                <IconButton
                                  size='small'
                                  disabled={!safeToReqImage}
                                  onClick={() =>
                                    onObligatoryOptionCommentChange(
                                      question.id,
                                      index,
                                      option.req_text,
                                      !option.req_image,
                                      option.req_finding,
                                    )
                                  }
                                >
                                  <PhotoQuestionIcon
                                    stroke={!option?.req_image ? '#B8BABF' : '#3B3C40'}
                                    sx={
                                      {
                                        ...customStyles.commentAlternativeBtn,
                                        ...customStyles.commentImageBtn,
                                      } as SxProps<Theme>
                                    }
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {findingsActivated && findingsCommentRequiredFlag && (
                              <Tooltip title={lang.form_builder.findingCommentBtnTooltip}>
                                <IconButton
                                  size='small'
                                  onClick={() =>
                                    onObligatoryOptionCommentChange(
                                      question.id,
                                      index,
                                      option.req_text,
                                      option.req_image,
                                      !option.req_finding,
                                    )
                                  }
                                >
                                  <AlertTriangleOutline
                                    sx={
                                      {
                                        ...customStyles.commentAlternativeBtn,
                                        ...customStyles.commentTextBtn,
                                        ...(option.req_finding && customStyles.commentTextRequired),
                                      } as SxProps<Theme>
                                    }
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </FormGroup>
                </FormControl>
              </Box>
            </Popover>
          </Box>
        )}
        {checkFlag('ChecklistConfig') && question.type === 'select_option_metadata_comments' && (
          <>
            <Box sx={customStyles.setting}>
              <Checkbox
                disableTouchRipple
                size={'small'}
                color={'default'}
                checked={question.disableGallery}
                onChange={() =>
                  onChecklistSettingsChange(
                    question.id,
                    !question.disableGallery,
                    checklistMaxImages,
                  )
                }
              />
              <Box sx={customStyles.label}>{lang.form_builder.disableGallery}</Box>
            </Box>
            <Box sx={customStyles.setting}>
              <TextField
                type='number'
                sx={customStyles.textFieldMargin}
                value={checklistMaxImages}
                defaultValue={1}
                onChange={handleOnChecklistSettingsChange}
                InputProps={{
                  inputProps: { min: 1, max: 10, sx: customStyles.textFieldInputPadding },
                }}
              />
              <Box sx={customStyles.label}>{lang.form_builder.maxImagesPerItem}</Box>
            </Box>
          </>
        )}
      </Box>
      {question.skipLogic === '1' && (
        <SkipLogicComponent
          questionId={question.id}
          skipLogicQuestion={question.skipLogicQuestion}
          skipLogicType={question.skipLogicType}
          skipLogicValue={question.skipLogicValue}
          skipLogicTarget={question.skipLogicTarget}
          onSkipSettingChange={onSkipSettingChange}
          logicQuestions={logicQuestions}
        />
      )}
      {question.type === 'multi_photo' && (
        <Box sx={customStyles.mobileOnlyAlert}>
          <InfoAlert message={lang.form_builder.mobileOnlyAlert} />
        </Box>
      )}
    </CardContent>
  );
});

export { QuestionSetting };
